import en from '../../../locales/en/index.json'
import fi from '../../../locales/fi/index.json'

// The cookie-consent preferences modal only renders these three sections,
// so its copy is keyed by this narrower union rather than the module-wide
// `ConsentCategory` (which also includes the UI-only `preferences`).
type TranslatedConsentCategory = 'necessary' | 'analytics' | 'marketing'

interface CookieConsentCopy {
  banner: {
    title: string
    description: string
    acceptAllBtn: string
    acceptNecessaryBtn: string
    showPreferencesBtn: string
  }
  preferences: {
    title: string
    savePreferencesBtn: string
    sections: Record<
      TranslatedConsentCategory,
      { title: string; description: string }
    >
  }
  floatingButton: {
    ariaLabel: string
  }
  footer: string
}

interface LocaleFile {
  cookieConsent: CookieConsentCopy
}

const LOCALES: Record<string, LocaleFile> = {
  en: en as LocaleFile,
  fi: fi as LocaleFile,
}

// vanilla-cookieconsent's `language.translations.<locale>` shape
// (consentModal/preferencesModal), built from src/locales/{en,fi}/index.json's
// `cookieConsent` namespace rather than a second, hand-duplicated copy.
export interface CookieConsentTranslation {
  consentModal: {
    title: string
    description: string
    acceptAllBtn: string
    acceptNecessaryBtn: string
    showPreferencesBtn: string
    footer: string
  }
  preferencesModal: {
    title: string
    savePreferencesBtn: string
    sections: {
      title: string
      description: string
      linkedCategory?: TranslatedConsentCategory
    }[]
  }
}

const buildTranslation = (locale: LocaleFile): CookieConsentTranslation => {
  const { banner, preferences, footer } = locale.cookieConsent
  return {
    consentModal: {
      title: banner.title,
      description: banner.description,
      acceptAllBtn: banner.acceptAllBtn,
      acceptNecessaryBtn: banner.acceptNecessaryBtn,
      showPreferencesBtn: banner.showPreferencesBtn,
      footer,
    },
    preferencesModal: {
      title: preferences.title,
      savePreferencesBtn: preferences.savePreferencesBtn,
      sections: [
        {
          title: preferences.sections.necessary.title,
          description: preferences.sections.necessary.description,
          linkedCategory: 'necessary',
        },
        {
          title: preferences.sections.analytics.title,
          description: preferences.sections.analytics.description,
          linkedCategory: 'analytics',
        },
        {
          title: preferences.sections.marketing.title,
          description: preferences.sections.marketing.description,
          linkedCategory: 'marketing',
        },
      ],
    },
  }
}

export const getConsentTranslations = (): Record<
  string,
  CookieConsentTranslation
> => ({
  en: buildTranslation(LOCALES.en),
  fi: buildTranslation(LOCALES.fi),
})

export const getFloatingButtonAriaLabel = (locale: string): string =>
  (LOCALES[locale] ?? LOCALES.fi).cookieConsent.floatingButton.ariaLabel
